import { useRef, useState, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Download, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

// Local same-origin logo prevents canvas from being tainted (cross-origin).
// Fallback: no embedded logo if local fetch fails.
const LOCAL_LOGO = "/logo.png";

export default function QrCodeDialog({ open, onOpenChange, ride, url }) {
  const canvasWrapRef = useRef(null);
  const [logoOk, setLogoOk] = useState(false);

  useEffect(() => {
    if (!open) return;
    const img = new Image();
    img.onload = () => setLogoOk(true);
    img.onerror = () => setLogoOk(false);
    img.src = LOCAL_LOGO;
  }, [open]);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `role-${(ride?.name || "speedfreaks").replace(/\s+/g, "-").toLowerCase()}-qrcode.png`;
      link.click();
    } catch (err) {
      toast.error("Não foi possível baixar. Faça uma captura de tela do QR.");
      // eslint-disable-next-line no-console
      console.error("QR download failed:", err);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const shareWhatsApp = () => {
    const text = `🏍️ ${ride?.name || "Rolê Speed Freaks"}\nConfirme presença: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#12141D] border-white/10"
        data-testid="qr-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase text-white">
            QR do Rolê
          </DialogTitle>
          <div className="text-white/60 text-sm">
            Escaneia com a câmera pra abrir os detalhes do rolê
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div
            ref={canvasWrapRef}
            className="flex justify-center bg-white p-6 rounded-sm"
            data-testid="qr-canvas"
          >
            {url && (
              <QRCodeCanvas
                value={url}
                size={240}
                level="H"
                includeMargin={false}
                imageSettings={
                  logoOk
                    ? {
                        src: LOCAL_LOGO,
                        height: 44,
                        width: 44,
                        excavate: true,
                        crossOrigin: "anonymous",
                      }
                    : undefined
                }
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={download}
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
              data-testid="qr-download-btn"
            >
              <Download size={14} /> Baixar PNG
            </button>
            <button
              onClick={copyLink}
              className="border border-white/15 hover:border-white px-4 py-2 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
              data-testid="qr-copy-btn"
            >
              <Copy size={14} /> Copiar Link
            </button>
            <button
              onClick={shareWhatsApp}
              className="border border-white/15 hover:border-[#00E5FF] px-4 py-2 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
              data-testid="qr-share-btn"
            >
              <Share2 size={14} /> WhatsApp
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
